interface FlowStep {
  id: string
  label: string
  title: string
  business: string
  technical: string
}

const searchSteps: FlowStep[] = [
  {
    id: 'user',
    label: '1. Request',
    title: 'Shopper searches',
    business: 'A shopper types a query or lands on a search URL from the header or a link.',
    technical: 'GET /s?q=mens+shoes with sort, facets, and page params parsed by GSSP.',
  },
  {
    id: 'gssp',
    label: '2. Server prefetch',
    title: 'Next.js prefetches on the server',
    business: 'The server fetches results before sending HTML — so the first page is not empty.',
    technical: 'getServerSideProps → isUnbxdEnabled() → fetchUnbxdSearch via unbxdBridge.ts.',
  },
  {
    id: 'bridge',
    label: '3. Bridge',
    title: 'VTEX bridge translates the request',
    business: 'A secure middleware app owns Unbxd credentials and orchestrates the search.',
    technical: 'POST to UNBXD_BRIDGE_URL → faststoreSearch resolver → validateSearchArgs → mapArgs.',
  },
  {
    id: 'unbxd',
    label: '4. Unbxd',
    title: 'Unbxd returns ranked products',
    business: 'Unbxd Search applies relevance, facets, and merchandising rules to the catalog index.',
    technical: 'GET search.unbxd.io/{apiKey}/{siteName}/search with filter[], sort, rows, start.',
  },
  {
    id: 'hydrate',
    label: '5. Hydration',
    title: 'VTEX catalog enriches products',
    business: 'Search results are merged with live prices, availability, and product details from VTEX.',
    technical: 'VtexCatalog.productsById (default hydrationKey: productId) + x-vtex-segment header.',
  },
  {
    id: 'html',
    label: '6. HTML response',
    title: 'Browser receives real product cards',
    business: 'Shoppers and crawlers see products immediately — even with JavaScript disabled.',
    technical: 'mapBridgeToSearchSsrPayload → SearchWrapper gate → CustomProductGallery SSR markup.',
  },
  {
    id: 'swr',
    label: '7. Client updates',
    title: 'Interactions fetch fresh data',
    business: 'Changing filters or sort updates the grid without a full page reload.',
    technical: 'useHydrateSearchGallery seeds SWR → useCreateSearchGalleryPage → Query.search override.',
  },
]

/**
 * Renders the interactive search flow stepper.
 */
export function renderFlowExplorer(): string {
  const stepButtons = searchSteps
    .map(
      (s, i) =>
        `<button type="button" class="flow-explorer__step-btn${i === 0 ? ' is-active' : ''}" data-flow-step="${s.id}">
          ${s.label}
        </button>`
    )
    .join('')

  const cards = searchSteps
    .map(
      (s, i) =>
        `<div class="flow-explorer__card" data-flow-card="${s.id}"${i === 0 ? '' : ' hidden'}>
          <h4 class="flow-explorer__step-title">${s.title}</h4>
          <p class="flow-explorer__business">${s.business}</p>
          <p class="flow-explorer__technical"><strong>Technical:</strong> ${s.technical}</p>
        </div>`
    )
    .join('')

  return `<div class="flow-explorer" data-component="flow-explorer">
    <div class="flow-explorer__steps" role="tablist" aria-label="Search flow steps">${stepButtons}</div>
    <div class="flow-explorer__content">${cards}</div>
  </div>`
}

/**
 * Initializes flow explorer step navigation.
 */
export function initFlowExplorer(root: ParentNode = document): void {
  const container = root.querySelector('[data-component="flow-explorer"]')
  if (!container) return

  const buttons = container.querySelectorAll<HTMLButtonElement>('[data-flow-step]')
  const cards = container.querySelectorAll<HTMLElement>('[data-flow-card]')

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.flowStep
      buttons.forEach((b) => b.classList.toggle('is-active', b === btn))
      cards.forEach((c) => {
        const show = c.dataset.flowCard === id
        if (show) c.removeAttribute('hidden')
        else c.setAttribute('hidden', '')
      })
    })
  })
}
